import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  ButtonDropdown,
  CollectionPreferences,
  Container,
  Header,
  HelpPanel,
  Link,
  Popover,
  SpaceBetween,
  StatusIndicator,
  Table,
  Input,
  Icon,
} from "@amzn/awsui-components-react-v3";
import Skeleton from "@amzn/awsui-components-console/skeleton";
import { CredentialInfo, CredentialStatus } from "../services/idp/idp.types";
import {
  useRevokeCredentialMutation,
  useUpdateCredentialNicknameMutation,
} from "../services/idp/idp";
import { useNavigate } from "react-router-dom";
import { Alerts } from "../lib/types";
import { isWithinXDays, shortLocalDateTime } from "../utils/time";
import { PagePaths, YubicoPhishingResistantMfaUrl } from "../lib/constants";
import { RevokeCredentialConfirmationModal } from "./revoke-credential-confirmation-modal";
import {
  loadTablePreferences,
  saveTablePreferences,
  TablePreferencesConfig,
} from "../utils/table-preferences";
import { sanitizeNickname } from "../utils/input-validation";
import { getAppConfig } from "../services/config";

const ColumnIds = {
  ID: "id",
  NICKNAME: "nickname",
  SERIAL_NUMBER: "serialNumber",
  STATUS: "status",
  RP_ID: "rpId",
  REGISTERED_AT: "registeredAt",
  LAST_USED: "lastUsed",
} as const;

const ActionIds = {
  TOGGLE_INACTIVE: "toggle-inactive",
  REVOKE_CREDENTIAL: "revoke-credential",
} as const;

interface CredentialsTableProps {
  credentialsInfo?: Record<string, CredentialInfo>;
  alias?: string;
  isSelf?: boolean;
  onInfoClick: (panel: React.ReactNode) => void;
  showSkeleton: boolean;
  setAlerts: React.Dispatch<React.SetStateAction<Alerts>>;
}

const preferencesConfig: TablePreferencesConfig = {
  storageKey: "credentials-table-preferences",
  defaults: {
    contentDisplay: [
      { id: ColumnIds.ID, visible: true },
      { id: ColumnIds.NICKNAME, visible: true },
      { id: ColumnIds.SERIAL_NUMBER, visible: true },
      { id: ColumnIds.STATUS, visible: true },
      { id: ColumnIds.RP_ID, visible: getAppConfig().isProd },
      { id: ColumnIds.REGISTERED_AT, visible: true },
      { id: ColumnIds.LAST_USED, visible: true },
    ],
  },
};

const helpPanel = (
  <HelpPanel header={<Header variant="h2">FIDO2 Credentials</Header>}>
    <p>
      FIDO2 credentials are hardware-backed security keys (e.g. YubiKeys) used
      for passwordless authentication. Each registered key is cryptographically
      bound to a specific domain and user account, providing{" "}
      <Link external href={YubicoPhishingResistantMfaUrl}>
        phishing-resistant
      </Link>
      access to Frontier applications.
    </p>
    <Box variant="h5">Nickname</Box>
    <p>
      Nicknames can be set for credentials to help you more easily distinguish
      between them. You can set or change a credential nickname by hovering over
      the nickname cell for the credential and clicking the{" "}
      <Icon variant="link" name="edit" /> icon.
    </p>
    <Box variant="h5">Serial number</Box>
    <p>
      Serial numbers are only known for keys that have been provisioned by the
      Leo IAM team. Registrations of Amazon corporate YubiKeys do not provide us
      with visibility to the serial numbers. Amazon FIPS keys do, however, have
      a unique AAGUID that allows us to identify them.
    </p>
    <Box variant="h5">Status</Box>
    <p>
      The status of the credential, either active or revoked (i.e. deleted).
    </p>
    <p>
      Revoked keys will not show in the table by default but can be viewed by
      expanding the Actions dropdown and clicking &quot;Show inactive&quot;.
    </p>
    <p>
      If you have lost a key, reset an active key by mistake, or have reached
      the maximum number of registrations and would like to register a new key,
      you can select a credential, expand the Actions dropdown, click
      &quot;Revoke credential&quot;, then click &quot;Register new key&quot;.
    </p>
    <Box variant="h5">RP ID</Box>
    <p>
      Frontier is currently in the process of migrating domains from
      astro.kpo.amazon to frontier.amazon — complicating FIDO2 key
      registrations, which are bound to the domain they are registered on.
    </p>
    <p>
      The RP ID field shows which domain the credential was registered on. When
      you see a Frontier login page, you can only authenticate with keys that
      have an RP ID matching the domain shown in the address bar of your
      browser.
    </p>
  </HelpPanel>
);

const normalizeCredentialId = (id: string) => id.substring(0, 6).toLowerCase();

const CredentialsTableSkeleton = () => (
  <Container
    data-testid="credentials-table-skeleton"
    header={<Header variant="h3">FIDO2 Credentials</Header>}
  >
    <SpaceBetween size="s">
      <Skeleton variant="text-body-m" width="100%" />
      <Skeleton variant="text-body-m" width="100%" />
    </SpaceBetween>
  </Container>
);

export const CredentialsTable = ({
  credentialsInfo,
  alias,
  isSelf = false,
  onInfoClick,
  showSkeleton,
  setAlerts,
}: CredentialsTableProps) => {
  const navigate = useNavigate();
  const [showInactive, setShowInactive] = useState(false);
  const [selectedItems, setSelectedItems] = useState<
    (CredentialInfo & { id: string })[]
  >([]);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeCredential, { isLoading: isRevoking }] =
    useRevokeCredentialMutation();
  const [updateNickname] = useUpdateCredentialNicknameMutation();
  const [nicknameOverrides, setNicknameOverrides] = useState<
    Record<string, string>
  >({});
  const [preferences, setPreferences] = useState(preferencesConfig.defaults);

  useEffect(() => {
    const { preferences: loaded, failed } =
      loadTablePreferences(preferencesConfig);
    setPreferences(loaded);
    if (failed) {
      setAlerts?.((prev) => ({
        ...prev,
        loadPreferencesWarning: {
          message: "Failed to load table preferences. Resetting to defaults.",
          ariaLabel: "Warning",
          autoDismiss: true,
          type: "warning",
        },
      }));
    }
  }, [setAlerts]);

  if (showSkeleton) {
    return <CredentialsTableSkeleton />;
  }

  const allItems = Object.entries(credentialsInfo ?? {})
    .map(([id, cred]) => ({
      id,
      ...cred,
      nickname: nicknameOverrides[id] ?? cred.nickname,
    }))
    .sort(
      (a, b) => new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime(),
    );

  const items = showInactive
    ? allItems
    : allItems.filter((item) => item.status === CredentialStatus.Active);

  const handleRevokeConfirm = (reason: string) => {
    if (!selectedItems.length || !alias) return;
    revokeCredential({
      alias,
      credentialId: selectedItems[0].id,
      reason,
    })
      .unwrap()
      .then(() => {
        setAlerts?.((prev) => ({
          ...prev,
          // use the same alert key as the error alert so it will replace any error alert on success
          [`revokeCredential-${selectedItems[0].id}`]: {
            message: `Successfully revoked credential '${normalizeCredentialId(selectedItems[0].id)}'.`,
            ariaLabel: "Success",
            type: "success",
            autoDismiss: true,
          },
        }));
        setSelectedItems([]);
        setShowRevokeModal(false);
      })
      .catch(() => {
        setAlerts?.((prev) => ({
          ...prev,
          [`revokeCredential-${selectedItems[0].id}`]: {
            message: `Failed to revoke credential '${normalizeCredentialId(selectedItems[0].id)}'. Please try again.`,
            ariaLabel: "Error",
            autoDismiss: true,
            type: "error",
          },
        }));
        setShowRevokeModal(false);
      });
  };

  return (
    <>
      <Table
        data-testid="user-credentials-table"
        selectionType="single"
        trackBy="id"
        isItemDisabled={(item) => item.status === CredentialStatus.Revoked}
        onSelectionChange={({ detail }) =>
          setSelectedItems(detail.selectedItems)
        }
        selectedItems={selectedItems}
        submitEdit={async (item, column, newValue) => {
          if (column.id === ColumnIds.NICKNAME && alias) {
            const nickname = (newValue as string) ?? "";
            await updateNickname({
              alias,
              credentialId: item.id,
              nickname,
            })
              .unwrap()
              .then(() => {
                setAlerts?.((prev) => ({
                  ...prev,
                  [`updateNickname-${item.id}`]: {
                    message: `Successfully updated the nickname of credential '${normalizeCredentialId(item.id)}'.`,
                    ariaLabel: "Success",
                    type: "success",
                    autoDismiss: true,
                  },
                }));
                setNicknameOverrides((prev) => ({
                  ...prev,
                  [item.id]: nickname,
                }));
              })
              .catch((error) => {
                setAlerts?.((prev) => ({
                  ...prev,
                  [`updateNickname-${item.id}`]: {
                    message: `Failed to update the nickname of credential '${normalizeCredentialId(item.id)}'. Please try again.`,
                    ariaLabel: "Error",
                    autoDismiss: true,
                    type: "error",
                  },
                }));
                throw error; // keeps the cell in edit mode
              });
          }
        }}
        header={
          <Header
            variant="h3"
            counter={`(${items.length})`}
            info={
              <Button
                variant="inline-link"
                onClick={() => onInfoClick(helpPanel)}
              >
                Info
              </Button>
            }
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <ButtonDropdown
                  items={[
                    {
                      id: ActionIds.TOGGLE_INACTIVE,
                      text: showInactive ? "Hide inactive" : "Show inactive",
                    },
                    {
                      id: ActionIds.REVOKE_CREDENTIAL,
                      text: "Revoke credential",
                      disabled: !selectedItems.length || !alias,
                    },
                  ]}
                  loading={isRevoking}
                  onItemClick={({ detail }) => {
                    switch (detail.id) {
                      case ActionIds.REVOKE_CREDENTIAL:
                        selectedItems.length && setShowRevokeModal(true);
                        break;
                      case ActionIds.TOGGLE_INACTIVE:
                        setShowInactive((prev) => !prev);
                        break;
                      default:
                        setAlerts?.((prev) => ({
                          ...prev,
                          invalidAction: {
                            message: "Invalid action.",
                            ariaLabel: "Error",
                            type: "error",
                          },
                        }));
                    }
                  }}
                >
                  Actions
                </ButtonDropdown>
                <Button
                  variant="primary"
                  disabled={!isSelf}
                  disabledReason="Cannot register keys on behalf of users"
                  onClick={() => navigate(PagePaths.REGISTER_MFA)}
                >
                  Register new key
                </Button>
                <CollectionPreferences
                  title="Preferences"
                  confirmLabel="Confirm"
                  cancelLabel="Cancel"
                  preferences={preferences}
                  onConfirm={({ detail }) => {
                    setPreferences(detail);
                    saveTablePreferences(preferencesConfig, detail);
                  }}
                  contentDisplayPreference={{
                    title: "Column preferences",
                    options: [
                      { id: ColumnIds.ID, label: "ID" },
                      {
                        id: ColumnIds.NICKNAME,
                        label: "Nickname",
                        alwaysVisible: true,
                      },
                      { id: ColumnIds.SERIAL_NUMBER, label: "Serial number" },
                      {
                        id: ColumnIds.STATUS,
                        label: "Status",
                        alwaysVisible: true,
                      },
                      { id: ColumnIds.RP_ID, label: "RP ID" },
                      { id: ColumnIds.REGISTERED_AT, label: "Registered" },
                      {
                        id: ColumnIds.LAST_USED,
                        label: "Last used",
                        alwaysVisible: true,
                      },
                    ],
                  }}
                />
              </SpaceBetween>
            }
          >
            FIDO2 Credentials
          </Header>
        }
        items={items}
        columnDisplay={preferences.contentDisplay}
        columnDefinitions={[
          {
            id: ColumnIds.ID,
            header: "ID",
            cell: (item) => normalizeCredentialId(item.id),
          },
          {
            id: ColumnIds.NICKNAME,
            header: "Nickname",
            cell: (item) => item.nickname || "-",
            editConfig: isSelf
              ? {
                  editingCell: (item, { currentValue, setValue }) => (
                    <Input
                      value={currentValue ?? item.nickname ?? ""}
                      onChange={({ detail }) => {
                        setValue(sanitizeNickname(detail.value));
                      }}
                      placeholder="Enter a nickname"
                      autoComplete={false}
                    />
                  ),
                }
              : undefined,
          },
          {
            id: ColumnIds.SERIAL_NUMBER,
            header: "Serial number",
            cell: (item) => (
              <Badge color={isNaN(Number(item.serialNumber)) ? "grey" : "blue"}>
                {item.serialNumber}
              </Badge>
            ),
          },
          {
            id: ColumnIds.STATUS,
            header: "Status",
            cell: (item) =>
              item.status === CredentialStatus.Revoked ? (
                <Popover
                  data-testid="credential-status-popover"
                  content={`Revoked at ${shortLocalDateTime(item.statusUpdateTimestamp)}`}
                  dismissButton={false}
                  size="medium"
                >
                  <StatusIndicator type="error">Revoked</StatusIndicator>
                </Popover>
              ) : (
                <StatusIndicator type="success">Active</StatusIndicator>
              ),
          },
          {
            id: ColumnIds.RP_ID,
            header: "RP ID",
            cell: (item) => item.rpId,
          },
          {
            id: ColumnIds.REGISTERED_AT,
            header: "Registered",
            cell: (item) => shortLocalDateTime(item.registeredAt),
          },
          {
            id: ColumnIds.LAST_USED,
            header: "Last used",
            cell: (item) =>
              item.status === CredentialStatus.Active &&
              !isWithinXDays(item.lastUsed, 30) ? (
                <StatusIndicator type="warning">
                  {shortLocalDateTime(item.lastUsed)}
                </StatusIndicator>
              ) : (
                shortLocalDateTime(item.lastUsed)
              ),
          },
        ]}
        empty={`No ${showInactive ? "" : "active "}credentials found`}
        footer={
          showInactive && (
            <Box textAlign="center" color="text-status-inactive">
              Showing inactive credentials.{" "}
              <Button
                variant="inline-link"
                onClick={() => setShowInactive(false)}
              >
                Hide
              </Button>
            </Box>
          )
        }
      />
      {alias && showRevokeModal && (
        <RevokeCredentialConfirmationModal
          alias={alias}
          credentialHint={normalizeCredentialId(selectedItems[0]?.id ?? "")}
          loading={isRevoking}
          onDismiss={() => setShowRevokeModal(false)}
          onConfirm={handleRevokeConfirm}
        />
      )}
    </>
  );
};
